import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { Viewmodel } from '../player/Viewmodel';
import {
  bakedAdsAttachPose,
  bakedAttachPose,
  bakedThenarShape,
  bakedThumbPose,
  type HandAttachPose,
  type HandSide,
  type ThenarShape,
  type ThumbPose,
} from '../player/ProcArms';
import {
  bakedReloadClip,
  DOUBLE_RELOAD_BEAT_ORDER,
  type DoubleReloadBeat,
  type DoubleReloadBeatId,
} from '../player/DoubleReloadClip';
import {
  bakedMagReloadClip,
  isMagReloadWeapon,
  PISTOL_RELOAD_BEAT_ORDER,
  type MagReloadWeaponId,
  type PistolReloadBeat,
  type PistolReloadBeatId,
} from '../player/PistolReloadClip';
import {
  applyM9GripShape,
  defaultM9GripShape,
  readM9GripShape,
  type M9GripShape,
} from '../player/ProcWeapons';
import type { PlayerController } from '../player/PlayerController';
import { AVATAR_PREVIEW_DIST, type PlayerAvatar } from '../player/PlayerAvatar';
import { WEAPON_ORDER, type WeaponId } from '../weapons/WeaponDefs';
import {
  bakedPunchClip,
  PUNCH_CLIP_LABELS,
  PUNCH_CLIP_ORDER,
  PUNCH_KEY_LABELS,
  PUNCH_KEY_ORDER,
  type PunchClipId,
  type PunchKey,
  type PunchKeyId,
} from '../player/PunchClip';

const LABELS: Record<WeaponId, string> = {
  pistol: 'Pistol',
  shotgun: 'Shotgun',
  sawedoff: 'Double',
  mp5: 'MP5',
  ak47: 'AK-47',
  punch: 'Fists',
  gl: 'GL',
  rpg: 'RPG',
};

interface SliderRow {
  range: HTMLInputElement;
  num: HTMLInputElement;
}

const FPS_TARGET = new THREE.Vector3(0, 0, -0.28);

/**
 * First-person hand-to-gun attach editor.
 * Open with `?calib=hands` or `?calib=hands&weapon=pistol`.
 */
export class HandCalib {
  private readonly panel: HTMLElement;
  private readonly snippetEl: HTMLPreElement;
  private readonly statusEl: HTMLElement;
  private readonly orbit: OrbitControls;
  private readonly onKey: (e: KeyboardEvent) => void;
  private readonly onWheel: (e: WheelEvent) => void;
  private readonly zoomDir = new THREE.Vector3();
  private weapon: WeaponId;
  private side: HandSide = 'R';
  private aiming = false;
  private active = false;
  private reloadBeat: DoubleReloadBeatId | PistolReloadBeatId | null = null;
  private punchClip: PunchClipId = 'jabR';
  private punchBeat: PunchKeyId | null = null;
  private rows: SliderRow[] = [];
  private gripRows: SliderRow[] = [];
  private gripShape: M9GripShape = defaultM9GripShape();
  private cloneOn = false;
  private cloneKneel = false;
  private viewMode: 'front' | 'side' | 'top' = 'front';
  private readonly lookTarget = new THREE.Vector3();
  private readonly lookMid = new THREE.Vector3();
  private readonly lookRight = new THREE.Vector3();
  private readonly lookFwd = new THREE.Vector3();

  constructor(
    private readonly viewmodel: Viewmodel,
    initial: WeaponId,
    canvas: HTMLElement,
    private readonly getAvatar: () => PlayerAvatar,
    private readonly player: PlayerController,
  ) {
    this.weapon = initial;
    this.panel = document.createElement('div');
    this.panel.id = 'hand-calib-panel';
    this.panel.className = 'vm-calib-panel hidden';
    this.panel.innerHTML = panelMarkup();
    document.getElementById('app')?.appendChild(this.panel);
    this.snippetEl = this.panel.querySelector('#hand-calib-snippet') as HTMLPreElement;
    this.statusEl = this.panel.querySelector('#hand-calib-status') as HTMLElement;
    this.orbit = new OrbitControls(this.viewmodel.camera, canvas);
    this.orbit.enabled = false;
    this.orbit.enableDamping = true;
    this.orbit.dampingFactor = 0.12;
    this.orbit.enablePan = true;
    this.orbit.screenSpacePanning = true;
    this.orbit.enableZoom = false;
    this.orbit.rotateSpeed = 0.85;
    this.orbit.minDistance = 0.05;
    this.orbit.maxDistance = 2.2;
    this.orbit.target.copy(FPS_TARGET);
    this.bindChrome();
    this.onKey = (e) => this.handleKey(e);
    this.onWheel = (e) => this.handleWheel(e);
  }

  get isActive(): boolean {
    return this.active;
  }

  get drawViewmodel(): boolean {
    return this.viewMode === 'front';
  }

  start(): void {
    this.equip(this.weapon, false);
    this.viewmodel.enableCalibFreeze(true, false, false);
    this.viewmodel.debugSetAds(0);
    this.resetView();
    this.orbit.enabled = true;
    this.buildSliders();
    this.buildThumbSliders();
    this.buildGripSliders();
    this.buildReloadSliders();
    this.fillPunchButtons();
    this.buildPunchSliders();
    this.refreshChrome();
    this.refreshSnippet();
    this.panel.classList.remove('hidden');
    this.active = true;
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('wheel', this.onWheel, { passive: false });
    this.statusEl.textContent =
      'Drag on the empty view to orbit. Wheel zooms. Right-drag pans. V resets to FPS view.';
  }

  update(): void {
    if (!this.active) return;
    this.orbit.update();
    this.refreshSnippet();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('wheel', this.onWheel);
    this.orbit.enabled = false;
    this.resetView();
    this.orbit.dispose();
    this.viewmodel.enableCalibFreeze(false);
    this.panel.remove();
    this.active = false;
  }

  private arms() {
    return this.viewmodel.getArms();
  }

  private pose(): HandAttachPose | null {
    return this.arms()?.poseOf(this.side, this.weapon, this.aiming) ?? null;
  }

  private bindChrome(): void {
    for (const id of WEAPON_ORDER) {
      this.panel.querySelector(`#hand-w-${id}`)?.addEventListener('click', () => this.equip(id));
    }
    this.panel.querySelector('#hand-side-r')?.addEventListener('click', () => this.setSide('R'));
    this.panel.querySelector('#hand-side-l')?.addEventListener('click', () => this.setSide('L'));
    this.panel.querySelector('#hand-hip')?.addEventListener('click', () => this.setAiming(false));
    this.panel.querySelector('#hand-ads')?.addEventListener('click', () => this.setAiming(true));
    this.panel.querySelector('#hand-copy')?.addEventListener('click', () => void this.copySnippet());
    this.panel.querySelector('#hand-reset-side')?.addEventListener('click', () => this.resetSide());
    this.panel.querySelector('#hand-reset-gun')?.addEventListener('click', () => this.resetGun());
    this.panel.querySelector('#hand-reset-thumb')?.addEventListener('click', () => this.resetThumb());
    this.panel.querySelector('#hand-reset-grip')?.addEventListener('click', () => this.resetGrip());
    this.panel.querySelector('#hand-reset-view')?.addEventListener('click', () => this.resetView());
    this.panel.querySelector('#hand-clone')?.addEventListener('click', () => this.toggleClone());
    this.panel.querySelector('#hand-clone-crouch')?.addEventListener('click', () => this.toggleCloneKneel());
    this.panel.querySelector('#hand-view-front')?.addEventListener('click', () => this.setViewMode('front'));
    this.panel.querySelector('#hand-view-side')?.addEventListener('click', () => this.setViewMode('side'));
    this.panel.querySelector('#hand-view-top')?.addEventListener('click', () => this.setViewMode('top'));
    this.panel.querySelector('#hand-play-reload')?.addEventListener('click', () => {
      this.reloadBeat = null;
      this.buildReloadSliders();
      this.refreshChrome();
      this.viewmodel.playReloadPreview();
    });
    this.panel.querySelector('#hand-play-punch')?.addEventListener('click', () => {
      this.punchBeat = null;
      this.buildPunchSliders();
      this.refreshChrome();
      this.viewmodel.playPunchPreview(this.punchClip);
    });
    this.panel.querySelector('#hand-reset-punch')?.addEventListener('click', () => this.resetPunchClip());
    this.panel.querySelector('#hand-reset-punch-beat')?.addEventListener('click', () => this.resetPunchBeat());
    this.panel.querySelector('#hand-reset-reload')?.addEventListener('click', () => this.resetReloadHold());
    this.panel.querySelector('#hand-reset-reload-beat')?.addEventListener('click', () => this.resetReloadBeat());
    this.fillReloadBeatButtons();
    this.fillPunchButtons();
  }

  private handleKey(e: KeyboardEvent): void {
    if (!this.active) return;
    if ((e.target as HTMLElement | null)?.closest?.('input, textarea, select')) return;
    if (e.code === 'KeyC') {
      e.preventDefault();
      this.toggleCloneKneel();
    }
    if (e.code === 'KeyA' || e.code === 'Tab') {
      e.preventDefault();
      this.setAiming(!this.aiming);
    }
    if (e.code === 'KeyQ') this.setSide('L');
    if (e.code === 'KeyE') this.setSide('R');
    if (e.code === 'KeyV' || e.code === 'Home') {
      e.preventDefault();
      this.setViewMode('front');
      this.resetView();
    }
    if (e.code === 'KeyH') {
      e.preventDefault();
      if (this.hasPunchClip()) this.cyclePunchBeat(1);
      else if (this.hasReloadClip()) this.cycleReloadBeat(1);
    }
    if (e.code === 'BracketLeft') {
      e.preventDefault();
      if (this.hasPunchClip()) this.cyclePunchBeat(-1);
      else if (this.hasReloadClip()) this.cycleReloadBeat(-1);
    }
    if (e.code === 'BracketRight') {
      e.preventDefault();
      if (this.hasPunchClip()) this.cyclePunchBeat(1);
      else if (this.hasReloadClip()) this.cycleReloadBeat(1);
    }
    if (e.code === 'KeyR' && this.hasPunchClip()) {
      e.preventDefault();
      this.punchBeat = null;
      this.buildPunchSliders();
      this.refreshChrome();
      this.viewmodel.playPunchPreview(this.punchClip);
    }
    const n = e.code.match(/^Digit([1-8])$/);
    if (n) {
      const id = WEAPON_ORDER[Number(n[1]) - 1];
      if (id) this.equip(id);
    }
  }

  private handleWheel(e: WheelEvent): void {
    if (!this.active) return;
    const t = e.target as HTMLElement | null;
    if (t?.closest?.('#hand-calib-panel')) return;
    e.preventDefault();
    const cam = this.orbit.object;
    this.zoomDir.subVectors(cam.position, this.orbit.target);
    let dist = this.zoomDir.length();
    if (dist < 1e-4) this.zoomDir.set(0, 0, 1);
    else this.zoomDir.multiplyScalar(1 / dist);
    const steps = e.deltaMode === WheelEvent.DOM_DELTA_LINE ? e.deltaY : e.deltaY / 100;
    dist = THREE.MathUtils.clamp(dist * Math.exp(steps * 0.12), this.orbit.minDistance, this.orbit.maxDistance);
    cam.position.copy(this.orbit.target).addScaledVector(this.zoomDir, dist);
    this.orbit.update();
  }

  private equip(id: WeaponId, rebuild = true): void {
    this.weapon = id;
    this.viewmodel.debugEquip(id);
    this.viewmodel.debugSetAds(this.aiming ? 1 : 0);
    history.replaceState(null, '', `?calib=hands&weapon=${id}`);
    if (rebuild) {
      this.buildSliders();
      this.buildThumbSliders();
      this.buildGripSliders();
      this.reloadBeat = null;
      this.punchBeat = null;
      this.fillReloadBeatButtons();
      this.fillPunchButtons();
      this.buildReloadSliders();
      this.buildPunchSliders();
      this.refreshChrome();
      this.refreshSnippet();
    }
  }

  private setSide(side: HandSide): void {
    this.side = side;
    this.buildSliders();
    this.buildThumbSliders();
    this.refreshChrome();
    this.refreshSnippet();
  }

  private setViewMode(mode: 'front' | 'side' | 'top'): void {
    if (this.viewMode === mode) return;
    this.viewMode = mode;
    const avatar = this.getAvatar();
    if (mode !== 'front') {
      if (!this.cloneOn) {
        this.cloneOn = true;
        avatar.previewInFront = true;
        avatar.previewKneel = this.cloneKneel;
      }
      avatar.showSelfHold = true;
      this.orbit.object = this.player.camera;
      this.orbit.minDistance = 0.45;
      this.orbit.maxDistance = 7;
      this.placeCompareCamera();
    } else {
      avatar.showSelfHold = false;
      this.orbit.object = this.viewmodel.camera;
      this.orbit.minDistance = 0.05;
      this.orbit.maxDistance = 2.2;
      this.orbit.minPolarAngle = 0;
      this.orbit.maxPolarAngle = Math.PI;
      this.player.syncCameraOnly();
      this.resetView();
    }
    this.refreshChrome();
    this.statusEl.textContent =
      mode === 'front'
        ? 'Front view — FPS overlay + clone ahead.'
        : mode === 'side'
          ? 'Side view — FPS hold and clone. Drag to orbit, wheel to zoom.'
          : 'Top view — FPS hold and clone. Drag to orbit, wheel to zoom.';
  }

  private placeCompareCamera(): void {
    const yaw = this.player.yaw;
    this.lookFwd.set(-Math.sin(yaw), 0, -Math.cos(yaw));
    this.lookRight.set(Math.cos(yaw), 0, -Math.sin(yaw));
    const eyeH = this.player.body.position.y + this.player.body.height - 0.12;
    this.lookMid.set(
      this.player.body.position.x + this.lookFwd.x * (AVATAR_PREVIEW_DIST * 0.5),
      eyeH,
      this.player.body.position.z + this.lookFwd.z * (AVATAR_PREVIEW_DIST * 0.5),
    );
    const cam = this.player.camera;
    cam.up.set(0, 1, 0);
    if (this.viewMode === 'top') {
      cam.position.set(
        this.lookMid.x + this.lookFwd.x * 0.08,
        this.lookMid.y + 2.1,
        this.lookMid.z + this.lookFwd.z * 0.08,
      );
      this.orbit.minPolarAngle = 0.06;
      this.orbit.maxPolarAngle = Math.PI * 0.48;
    } else {
      cam.position.copy(this.lookMid).addScaledVector(this.lookRight, 1.65);
      this.orbit.minPolarAngle = 0.12;
      this.orbit.maxPolarAngle = Math.PI - 0.12;
    }
    this.lookTarget.copy(this.lookMid);
    cam.lookAt(this.lookTarget);
    this.orbit.target.copy(this.lookMid);
    this.orbit.update();
  }

  private toggleClone(): void {
    this.cloneOn = !this.cloneOn;
    const avatar = this.getAvatar();
    avatar.previewInFront = this.cloneOn;
    avatar.previewKneel = this.cloneOn && this.cloneKneel;
    if (!this.cloneOn) avatar.root.visible = false;
    this.refreshChrome();
    this.statusEl.textContent = this.cloneOn
      ? 'Clone is in front of you — same FPS hold. Hide it if it blocks the gun.'
      : 'Clone hidden.';
  }

  private toggleCloneKneel(): void {
    this.cloneKneel = !this.cloneKneel;
    const avatar = this.getAvatar();
    avatar.previewKneel = this.cloneOn && this.cloneKneel;
    if (!this.cloneOn) {
      this.cloneOn = true;
      avatar.previewInFront = true;
      avatar.previewKneel = this.cloneKneel;
    }
    this.refreshChrome();
    this.statusEl.textContent = this.cloneKneel
      ? 'Clone kneeling fire stance.'
      : 'Clone standing.';
  }

  private setAiming(on: boolean): void {
    this.aiming = on;
    this.viewmodel.debugSetAds(on ? 1 : 0);
    this.unparkReload();
    if (this.punchBeat) this.reparkPunch();
    this.buildSliders();
    this.refreshChrome();
    this.refreshSnippet();
  }

  private refreshChrome(): void {
    for (const id of WEAPON_ORDER) {
      this.panel.querySelector(`#hand-w-${id}`)?.classList.toggle('active', this.weapon === id);
    }
    this.panel.querySelector('#hand-side-r')?.classList.toggle('active', this.side === 'R');
    this.panel.querySelector('#hand-side-l')?.classList.toggle('active', this.side === 'L');
    this.panel.querySelector('#hand-hip')?.classList.toggle('active', !this.aiming);
    this.panel.querySelector('#hand-ads')?.classList.toggle('active', this.aiming);
    const cloneBtn = this.panel.querySelector('#hand-clone');
    if (cloneBtn) {
      cloneBtn.classList.toggle('active', this.cloneOn);
      cloneBtn.textContent = this.cloneOn ? 'Hide clone' : 'Show clone';
    }
    const crouchBtn = this.panel.querySelector('#hand-clone-crouch');
    if (crouchBtn) {
      crouchBtn.classList.toggle('active', this.cloneKneel);
      crouchBtn.textContent = this.cloneKneel ? 'Stand clone' : 'Crouch clone';
    }
    this.panel.querySelector('#hand-view-front')?.classList.toggle('active', this.viewMode === 'front');
    this.panel.querySelector('#hand-view-side')?.classList.toggle('active', this.viewMode === 'side');
    this.panel.querySelector('#hand-view-top')?.classList.toggle('active', this.viewMode === 'top');
    const title = this.panel.querySelector('#hand-calib-title');
    if (title) title.textContent = `Hands — ${LABELS[this.weapon]} ${this.side === 'R' ? 'right' : 'left'}`;
    const sec = this.panel.querySelector('#hand-section-title');
    if (sec) {
      sec.textContent = `${this.side === 'R' ? 'Right hand on grip_r' : 'Left hand on grip_l'} — ${this.aiming ? 'ADS' : 'hip'}`;
    }
    const thumbSec = this.panel.querySelector('#hand-thumb-title');
    if (thumbSec) {
      thumbSec.textContent =
        this.side === 'R' ? 'Right thumb joints' : 'Left thumb joints';
    }
    this.panel.querySelector('#hand-grip-section')?.classList.toggle('hidden', this.weapon !== 'pistol');
    this.panel.querySelector('#hand-reload-section')?.classList.toggle(
      'hidden',
      this.weapon !== 'sawedoff' && !isMagReloadWeapon(this.weapon),
    );
    this.panel.querySelector('#hand-play-reload')?.classList.toggle(
      'hidden',
      this.weapon !== 'sawedoff' && this.weapon !== 'shotgun' && !isMagReloadWeapon(this.weapon),
    );
    this.panel.querySelector('#hand-punch-section')?.classList.toggle('hidden', this.weapon !== 'punch');
    this.panel.querySelector('#hand-play-punch')?.classList.toggle('hidden', this.weapon !== 'punch');
    for (const id of PUNCH_CLIP_ORDER) {
      this.panel.querySelector(`#hand-punch-clip-${id}`)?.classList.toggle('active', this.punchClip === id);
    }
    for (const id of PUNCH_KEY_ORDER) {
      this.panel.querySelector(`#hand-punch-beat-${id}`)?.classList.toggle('active', this.punchBeat === id);
    }
    const hint = this.panel.querySelector('#hand-reload-hint');
    if (hint) {
      hint.textContent = isMagReloadWeapon(this.weapon)
        ? 'Each beat is a pose. Playback interpolates between them. Park one, set gun / dummy / hand / mag, copy snippet.'
        : 'Each beat is a pose. Playback interpolates between them. Park one, set gun / dummy / hand / shells, copy snippet.';
    }
    const gunTitle = this.panel.querySelector('#hand-reload-gun-title');
    if (gunTitle) {
      gunTitle.textContent = isMagReloadWeapon(this.weapon)
        ? 'Gun (whole model)'
        : 'Gun (whole model + barrel break)';
    }
    const dummyTitle = this.panel.querySelector('#hand-reload-dummy-title');
    if (dummyTitle) {
      dummyTitle.textContent = isMagReloadWeapon(this.weapon)
        ? 'Mag dummy (left hand target)'
        : 'Hold-shells dummy (left hand target)';
    }
    const propTitle = this.panel.querySelector('#hand-reload-prop-title');
    if (propTitle) {
      propTitle.textContent = isMagReloadWeapon(this.weapon) ? 'Held mag (vs dummy)' : 'Shells in palm';
    }
    for (const id of [...DOUBLE_RELOAD_BEAT_ORDER, ...PISTOL_RELOAD_BEAT_ORDER]) {
      this.panel
        .querySelector(`#hand-beat-${id}`)
        ?.classList.toggle('active', this.reloadBeat === id);
    }
  }

  private buildSliders(): void {
    const host = this.panel.querySelector('#hand-calib-sliders') as HTMLElement;
    host.replaceChildren();
    this.rows = [];
    const p = this.pose();
    if (!p) return;
    const orig = this.aiming
      ? bakedAdsAttachPose(this.weapon, this.side)
      : bakedAttachPose(this.weapon, this.side);
    const defs: Array<{
      label: string;
      min: number;
      max: number;
      step: number;
      hint: string;
      key: keyof HandAttachPose;
    }> = [
      { label: 'Pos X', min: -0.22, max: 0.22, step: 0.001, hint: 'thumb ↔ pinky', key: 'x' },
      { label: 'Pos Y', min: -0.22, max: 0.22, step: 0.001, hint: 'into / off grip', key: 'y' },
      { label: 'Pos Z', min: -0.22, max: 0.22, step: 0.001, hint: 'wrist ↔ fingers', key: 'z' },
      { label: 'Pitch', min: -3.15, max: 3.15, step: 0.01, hint: 'hand rx', key: 'rx' },
      { label: 'Yaw', min: -3.15, max: 3.15, step: 0.01, hint: 'hand ry', key: 'ry' },
      { label: 'Roll', min: -3.15, max: 3.15, step: 0.01, hint: 'hand rz', key: 'rz' },
      { label: 'W Pitch', min: -3.15, max: 3.15, step: 0.01, hint: 'wrist rx', key: 'wx' },
      { label: 'W Yaw', min: -3.15, max: 3.15, step: 0.01, hint: 'wrist ry', key: 'wy' },
      { label: 'W Roll', min: -3.15, max: 3.15, step: 0.01, hint: 'wrist rz / twist', key: 'wz' },
      { label: 'Scale', min: 0.5, max: 2.4, step: 0.01, hint: 'hand size', key: 'scale' },
      { label: 'Curl', min: -1.6, max: 1.0, step: 0.01, hint: 'all joints wrap', key: 'curl' },
      { label: 'Knuckle', min: -1.6, max: 1.0, step: 0.01, hint: 'ring+pinky first joint extra wrap', key: 'knuckle' },
    ];
    for (const d of defs) {
      this.rows.push(this.mountSlider(host, d, p, orig));
    }
  }

  private hasReloadClip(): boolean {
    return this.weapon === 'sawedoff' || isMagReloadWeapon(this.weapon);
  }

  private hasPunchClip(): boolean {
    return this.weapon === 'punch';
  }

  private fillPunchButtons(): void {
    const clipHost = this.panel.querySelector('#hand-punch-clips') as HTMLElement | null;
    const beatHost = this.panel.querySelector('#hand-punch-beats') as HTMLElement | null;
    if (clipHost) {
      clipHost.replaceChildren();
      for (const id of PUNCH_CLIP_ORDER) {
        const btn = document.createElement('button');
        btn.id = `hand-punch-clip-${id}`;
        btn.className = 'calib-btn';
        btn.type = 'button';
        btn.textContent = PUNCH_CLIP_LABELS[id];
        btn.addEventListener('click', () => this.selectPunchClip(id));
        clipHost.appendChild(btn);
      }
    }
    if (beatHost) {
      beatHost.replaceChildren();
      for (const id of PUNCH_KEY_ORDER) {
        const btn = document.createElement('button');
        btn.id = `hand-punch-beat-${id}`;
        btn.className = 'calib-btn calib-btn-beat';
        btn.type = 'button';
        btn.textContent = PUNCH_KEY_LABELS[id];
        btn.addEventListener('click', () => this.selectPunchBeat(id));
        beatHost.appendChild(btn);
      }
    }
  }

  private selectPunchClip(id: PunchClipId): void {
    this.punchClip = id;
    if (this.punchBeat) this.reparkPunch();
    else this.viewmodel.clearPunchFreeze();
    this.buildPunchSliders();
    this.refreshChrome();
    this.statusEl.textContent = `Punch clip: ${PUNCH_CLIP_LABELS[id]} — park Init / Pos 1 / Pos 2 / End.`;
  }

  private selectPunchBeat(id: PunchKeyId): void {
    if (!this.hasPunchClip()) return;
    if (this.punchBeat === id) {
      this.unparkPunch();
      return;
    }
    this.punchBeat = id;
    this.viewmodel.freezePunchBeat(this.punchClip, id);
    this.buildPunchSliders();
    this.refreshChrome();
    this.statusEl.textContent = `Punch ${PUNCH_CLIP_LABELS[this.punchClip]} / ${PUNCH_KEY_LABELS[id]} — set both fists. [ ] or H steps. R plays.`;
  }

  private unparkPunch(): void {
    if (!this.punchBeat && !this.viewmodel.punchHoldFrozen) return;
    this.punchBeat = null;
    this.viewmodel.clearPunchFreeze();
    this.buildPunchSliders();
    this.refreshChrome();
    this.statusEl.textContent = this.aiming
      ? 'Guard stance — click a punch beat to park.'
      : 'Hip stance — click a punch beat to park.';
  }

  private cyclePunchBeat(dir: number): void {
    const n = PUNCH_KEY_ORDER.length;
    if (!this.punchBeat) {
      this.selectPunchBeat(dir >= 0 ? PUNCH_KEY_ORDER[0]! : PUNCH_KEY_ORDER[n - 1]!);
      return;
    }
    const i = PUNCH_KEY_ORDER.indexOf(this.punchBeat);
    this.selectPunchBeat(PUNCH_KEY_ORDER[(i + dir + n) % n]!);
  }

  private resetPunchClip(): void {
    this.viewmodel.resetPunchClip(this.punchClip);
    this.buildPunchSliders();
    this.refreshSnippet();
    this.reparkPunch();
  }

  private resetPunchBeat(): void {
    if (!this.punchBeat) return;
    this.viewmodel.resetPunchBeat(this.punchClip, this.punchBeat);
    this.buildPunchSliders();
    this.refreshSnippet();
    this.reparkPunch();
  }

  private reparkPunch(): void {
    if (!this.punchBeat) return;
    this.viewmodel.freezePunchBeat(this.punchClip, this.punchBeat);
  }

  private buildPunchSliders(): void {
    const rightHost = this.panel.querySelector('#hand-punch-right-sliders') as HTMLElement | null;
    const leftHost = this.panel.querySelector('#hand-punch-left-sliders') as HTMLElement | null;
    if (!rightHost || !leftHost) return;
    rightHost.replaceChildren();
    leftHost.replaceChildren();
    if (!this.punchBeat) return;
    const beat = this.viewmodel.punchKey(this.punchClip, this.punchBeat);
    const orig = bakedPunchClip(this.punchClip).keys.find((k) => k.id === this.punchBeat) ?? null;
    if (!beat || !orig) return;

    const idx = PUNCH_KEY_ORDER.indexOf(this.punchBeat);
    const prev = idx > 0 ? this.viewmodel.punchKey(this.punchClip, PUNCH_KEY_ORDER[idx - 1]!) : null;
    const next =
      idx >= 0 && idx < PUNCH_KEY_ORDER.length - 1
        ? this.viewmodel.punchKey(this.punchClip, PUNCH_KEY_ORDER[idx + 1]!)
        : null;
    const tMin = prev ? prev.t + 0.01 : 0;
    const tMax = next ? next.t - 0.01 : 1;
    this.mountNum(rightHost, 'Time', tMin, tMax, 0.01, 'when this pose hits (0 = start, 1 = end)', () => beat.t, (n) => { beat.t = n; }, orig.t);
    this.mountFistSliders(rightHost, 'R', beat, orig);
    this.mountFistSliders(leftHost, 'L', beat, orig);
  }

  private mountFistSliders(host: HTMLElement, side: 'R' | 'L', beat: PunchKey, orig: PunchKey): void {
    const fist = side === 'R' ? beat.right : beat.left;
    const o = side === 'R' ? orig.right : orig.left;
    this.mountNum(host, 'Pos X', -0.8, 0.8, 0.001, 'fist target X', () => fist.x, (n) => { fist.x = n; }, o.x);
    this.mountNum(host, 'Pos Y', -0.8, 0.8, 0.001, 'fist target Y', () => fist.y, (n) => { fist.y = n; }, o.y);
    this.mountNum(host, 'Pos Z', -1.2, 0.4, 0.001, 'fist target Z (toward camera +)', () => fist.z, (n) => { fist.z = n; }, o.z);
    this.mountNum(host, 'Pitch', -3.15, 3.15, 0.01, 'fist rx', () => fist.rx, (n) => { fist.rx = n; }, o.rx);
    this.mountNum(host, 'Yaw', -3.15, 3.15, 0.01, 'fist ry', () => fist.ry, (n) => { fist.ry = n; }, o.ry);
    this.mountNum(host, 'Roll', -3.15, 3.15, 0.01, 'fist rz', () => fist.rz, (n) => { fist.rz = n; }, o.rz);
  }

  private fillReloadBeatButtons(): void {
    const host = this.panel.querySelector('#hand-reload-beats') as HTMLElement | null;
    if (!host) return;
    host.replaceChildren();
    const mag = isMagReloadWeapon(this.weapon);
    const order = mag
      ? PISTOL_RELOAD_BEAT_ORDER
      : this.weapon === 'sawedoff'
        ? DOUBLE_RELOAD_BEAT_ORDER
        : [];
    const labels = mag
      ? Object.fromEntries(bakedMagReloadClip(this.weapon as MagReloadWeaponId).map((b) => [b.id, b.label]))
      : Object.fromEntries(bakedReloadClip().map((b) => [b.id, b.label]));
    for (const id of order) {
      const btn = document.createElement('button');
      btn.id = `hand-beat-${id}`;
      btn.className = 'calib-btn calib-btn-beat';
      btn.type = 'button';
      btn.textContent = labels[id] ?? id;
      btn.addEventListener('click', () => this.selectReloadBeat(id));
      host.appendChild(btn);
    }
  }

  private selectReloadBeat(id: DoubleReloadBeatId | PistolReloadBeatId): void {
    if (!this.hasReloadClip()) return;
    if (this.reloadBeat === id) {
      this.unparkReload();
      return;
    }
    this.reloadBeat = id;
    if (this.weapon === 'sawedoff') {
      if (id === 'pocket' || id === 'hold' || id === 'press' || id === 'clear') {
        if (this.side !== 'L') this.setSide('L');
      }
      this.viewmodel.freezeReloadBeat(id as DoubleReloadBeatId);
    } else {
      if (id === 'away' || id === 'drop' || id === 'fetch' || id === 'seat') {
        if (this.side !== 'L') this.setSide('L');
      }
      this.viewmodel.freezePistolReloadBeat(id as PistolReloadBeatId);
    }
    this.buildReloadSliders();
    this.refreshChrome();
    this.statusEl.textContent = `Reload beat: ${id} — set gun / dummy / hand / ${isMagReloadWeapon(this.weapon) ? 'mag' : 'shells'} here. Clip interpolates between beats. [ ] or H steps.`;
  }

  private unparkReload(): void {
    if (!this.reloadBeat && !this.viewmodel.reloadHoldFrozen) return;
    this.reloadBeat = null;
    this.viewmodel.clearReloadFreeze();
    this.buildReloadSliders();
    this.refreshChrome();
    this.statusEl.textContent = isMagReloadWeapon(this.weapon)
      ? this.weapon === 'pistol'
        ? 'Hip pose — left hand on grip_l. Click a reload beat to park.'
        : 'Hip pose — left hand on the forend. Click a reload beat to park.'
      : 'Hip pose — left hand on the forend. Click a reload beat to park.';
  }

  private cycleReloadBeat(dir: number): void {
    const order = isMagReloadWeapon(this.weapon) ? PISTOL_RELOAD_BEAT_ORDER : DOUBLE_RELOAD_BEAT_ORDER;
    const n = order.length;
    if (!this.reloadBeat) {
      this.selectReloadBeat(dir >= 0 ? order[0]! : order[n - 1]!);
      return;
    }
    const i = (order as readonly string[]).indexOf(this.reloadBeat);
    const next = order[(i + dir + n) % n]!;
    this.selectReloadBeat(next);
  }

  private resetReloadHold(): void {
    if (isMagReloadWeapon(this.weapon)) this.viewmodel.resetPistolReloadClip();
    else this.viewmodel.resetReloadClip();
    this.buildReloadSliders();
    this.refreshSnippet();
    this.reparkReload();
  }

  private resetReloadBeat(): void {
    if (!this.reloadBeat) return;
    if (isMagReloadWeapon(this.weapon)) this.viewmodel.resetPistolReloadBeat(this.reloadBeat as PistolReloadBeatId);
    else this.viewmodel.resetReloadBeat(this.reloadBeat as DoubleReloadBeatId);
    this.buildReloadSliders();
    this.refreshSnippet();
    this.reparkReload();
  }

  private reparkReload(): void {
    if (!this.reloadBeat) return;
    if (isMagReloadWeapon(this.weapon)) this.viewmodel.freezePistolReloadBeat(this.reloadBeat as PistolReloadBeatId);
    else this.viewmodel.freezeReloadBeat(this.reloadBeat as DoubleReloadBeatId);
  }

  private buildReloadSliders(): void {
    const gunHost = this.panel.querySelector('#hand-reload-gun-sliders') as HTMLElement | null;
    const dummyHost = this.panel.querySelector('#hand-reload-dummy-sliders') as HTMLElement | null;
    const handHost = this.panel.querySelector('#hand-reload-hand-sliders') as HTMLElement | null;
    const shellHost = this.panel.querySelector('#hand-reload-shell-sliders') as HTMLElement | null;
    if (!gunHost || !dummyHost || !handHost || !shellHost) return;
    gunHost.replaceChildren();
    dummyHost.replaceChildren();
    handHost.replaceChildren();
    shellHost.replaceChildren();
    if (!this.reloadBeat) return;
    if (isMagReloadWeapon(this.weapon)) {
      this.buildPistolReloadSliders(gunHost, dummyHost, handHost, shellHost);
      return;
    }
    if (this.weapon !== 'sawedoff') return;
    this.buildDoubleReloadSliders(gunHost, dummyHost, handHost, shellHost);
  }

  private buildDoubleReloadSliders(
    gunHost: HTMLElement,
    dummyHost: HTMLElement,
    handHost: HTMLElement,
    shellHost: HTMLElement,
  ): void {
    const id = this.reloadBeat as DoubleReloadBeatId;
    const beat = this.viewmodel.reloadBeat(id);
    const orig = bakedReloadClip().find((b) => b.id === id);
    if (!beat || !orig) return;

    const idx = DOUBLE_RELOAD_BEAT_ORDER.indexOf(id);
    const prev = idx > 0 ? this.viewmodel.reloadBeat(DOUBLE_RELOAD_BEAT_ORDER[idx - 1]!) : null;
    const next =
      idx >= 0 && idx < DOUBLE_RELOAD_BEAT_ORDER.length - 1
        ? this.viewmodel.reloadBeat(DOUBLE_RELOAD_BEAT_ORDER[idx + 1]!)
        : null;
    const tMin = prev ? prev.t + 0.01 : 0;
    const tMax = next ? next.t - 0.01 : 1;
    this.mountNum(gunHost, 'Time', tMin, tMax, 0.01, 'when this pose hits (0 = start, 1 = end)', () => beat.t, (n) => { beat.t = n; }, orig.t);
    this.mountNum(gunHost, 'Pos X', -0.3, 0.3, 0.001, 'gun offset X (left -)', () => beat.gun.x, (n) => { beat.gun.x = n; }, orig.gun.x);
    this.mountNum(gunHost, 'Pos Y', -0.3, 0.3, 0.001, 'gun offset Y (up +)', () => beat.gun.y, (n) => { beat.gun.y = n; }, orig.gun.y);
    this.mountNum(gunHost, 'Pos Z', -0.3, 0.3, 0.001, 'gun offset Z (toward camera +)', () => beat.gun.z, (n) => { beat.gun.z = n; }, orig.gun.z);
    this.mountNum(gunHost, 'Pitch', -1.6, 1.6, 0.01, 'whole gun pitch (muzzle up +)', () => beat.gun.pitch, (n) => { beat.gun.pitch = n; }, orig.gun.pitch);
    this.mountNum(gunHost, 'Yaw', -1.6, 1.6, 0.01, 'whole gun yaw (left +)', () => beat.gun.yaw, (n) => { beat.gun.yaw = n; }, orig.gun.yaw);
    this.mountNum(gunHost, 'Roll', -1.6, 1.6, 0.01, 'whole gun roll', () => beat.gun.roll, (n) => { beat.gun.roll = n; }, orig.gun.roll);
    this.mountNum(gunHost, 'Break', -1.4, 0.4, 0.01, 'barrel hinge (negative = muzzles down)', () => beat.breakX, (n) => { beat.breakX = n; }, orig.breakX);

    this.mountNum(dummyHost, 'Leave forend', 0, 1, 0.01, '0 = glued to grip_l, 1 = dummy', () => beat.dummyBlend, (n) => { beat.dummyBlend = n; }, orig.dummyBlend);
    this.mountNum(dummyHost, 'Follow forend', 0, 1, 0.01, '1 = keep hip hand orientation (xyz is an offset)', () => beat.followGrip, (n) => { beat.followGrip = n; }, orig.followGrip);
    this.mountNum(dummyHost, 'Pos X', -0.4, 0.4, 0.001, 'offset X when following, else pose X', () => beat.dummy.x, (n) => { beat.dummy.x = n; }, orig.dummy.x);
    this.mountNum(dummyHost, 'Pos Y', -0.4, 0.4, 0.001, 'offset Y when following, else pose Y', () => beat.dummy.y, (n) => { beat.dummy.y = n; }, orig.dummy.y);
    this.mountNum(dummyHost, 'Pos Z', -0.4, 0.4, 0.001, 'offset Z when following, else pose Z', () => beat.dummy.z, (n) => { beat.dummy.z = n; }, orig.dummy.z);
    this.mountNum(dummyHost, 'Pitch', -3.15, 3.15, 0.01, 'dummy pitch', () => beat.dummy.pitch, (n) => { beat.dummy.pitch = n; }, orig.dummy.pitch);
    this.mountNum(dummyHost, 'Yaw', -3.15, 3.15, 0.01, 'dummy yaw', () => beat.dummy.yaw, (n) => { beat.dummy.yaw = n; }, orig.dummy.yaw);
    this.mountNum(dummyHost, 'Roll', -3.15, 3.15, 0.01, 'dummy roll', () => beat.dummy.roll, (n) => { beat.dummy.roll = n; }, orig.dummy.roll);

    this.mountReloadHandSliders(handHost, beat.hand, orig.hand);

    this.mountNum(shellHost, 'Pos X', -0.08, 0.08, 0.001, 'across the palm', () => beat.pair.x, (n) => { beat.pair.x = n; }, orig.pair.x);
    this.mountNum(shellHost, 'Pos Y', -0.08, 0.08, 0.001, 'into the palm (-Y)', () => beat.pair.y, (n) => { beat.pair.y = n; }, orig.pair.y);
    this.mountNum(shellHost, 'Pos Z', -0.12, 0.08, 0.001, 'along the fingers (-Z)', () => beat.pair.z, (n) => { beat.pair.z = n; }, orig.pair.z);
    this.mountNum(shellHost, 'Pitch', -3.15, 3.15, 0.01, 'shell rx', () => beat.pair.rx, (n) => { beat.pair.rx = n; }, orig.pair.rx);
    this.mountNum(shellHost, 'Yaw', -3.15, 3.15, 0.01, 'shell ry', () => beat.pair.ry, (n) => { beat.pair.ry = n; }, orig.pair.ry);
    this.mountNum(shellHost, 'Roll', -3.15, 3.15, 0.01, 'shell rz', () => beat.pair.rz, (n) => { beat.pair.rz = n; }, orig.pair.rz);
    this.mountNum(shellHost, 'Show pair', 0, 1, 0.01, 'fresh shells in the hand', () => beat.pairVis, (n) => { beat.pairVis = n; }, orig.pairVis);
    this.mountNum(shellHost, 'In chamber', 0, 1, 0.01, 'shells visible in the gun', () => beat.chamberVis, (n) => { beat.chamberVis = n; }, orig.chamberVis);
  }

  private buildPistolReloadSliders(
    gunHost: HTMLElement,
    dummyHost: HTMLElement,
    handHost: HTMLElement,
    shellHost: HTMLElement,
  ): void {
    const id = this.reloadBeat as PistolReloadBeatId;
    if (!isMagReloadWeapon(this.weapon)) return;
    const beat = this.viewmodel.pistolReloadBeat(id);
    const orig = bakedMagReloadClip(this.weapon).find((b) => b.id === id);
    if (!beat || !orig) return;

    const idx = PISTOL_RELOAD_BEAT_ORDER.indexOf(id);
    const prev = idx > 0 ? this.viewmodel.pistolReloadBeat(PISTOL_RELOAD_BEAT_ORDER[idx - 1]!) : null;
    const next =
      idx >= 0 && idx < PISTOL_RELOAD_BEAT_ORDER.length - 1
        ? this.viewmodel.pistolReloadBeat(PISTOL_RELOAD_BEAT_ORDER[idx + 1]!)
        : null;
    const tMin = prev ? prev.t + 0.01 : 0;
    const tMax = next ? next.t - 0.01 : 1;
    this.mountNum(gunHost, 'Time', tMin, tMax, 0.01, 'when this pose hits (0 = start, 1 = end)', () => beat.t, (n) => { beat.t = n; }, orig.t);
    this.mountNum(gunHost, 'Pos X', -0.3, 0.3, 0.001, 'gun offset X (left -)', () => beat.gun.x, (n) => { beat.gun.x = n; }, orig.gun.x);
    this.mountNum(gunHost, 'Pos Y', -0.3, 0.3, 0.001, 'gun offset Y (up +)', () => beat.gun.y, (n) => { beat.gun.y = n; }, orig.gun.y);
    this.mountNum(gunHost, 'Pos Z', -0.3, 0.3, 0.001, 'gun offset Z (toward camera +)', () => beat.gun.z, (n) => { beat.gun.z = n; }, orig.gun.z);
    this.mountNum(gunHost, 'Pitch', -1.6, 1.6, 0.01, 'whole gun pitch (muzzle up +)', () => beat.gun.pitch, (n) => { beat.gun.pitch = n; }, orig.gun.pitch);
    this.mountNum(gunHost, 'Yaw', -1.6, 1.6, 0.01, 'whole gun yaw (left +)', () => beat.gun.yaw, (n) => { beat.gun.yaw = n; }, orig.gun.yaw);
    this.mountNum(gunHost, 'Roll', -1.6, 1.6, 0.01, 'whole gun roll', () => beat.gun.roll, (n) => { beat.gun.roll = n; }, orig.gun.roll);

    this.mountNum(dummyHost, 'Leave grip', 0, 1, 0.01, '0 = glued to grip_l, 1 = dummy', () => beat.dummyBlend, (n) => { beat.dummyBlend = n; }, orig.dummyBlend);
    this.mountNum(dummyHost, 'Follow grip', 0, 1, 0.01, '1 = keep hip hand orientation (xyz is an offset)', () => beat.followGrip, (n) => { beat.followGrip = n; }, orig.followGrip);
    this.mountNum(dummyHost, 'Pos X', -0.4, 0.4, 0.001, 'offset X when following, else pose X', () => beat.dummy.x, (n) => { beat.dummy.x = n; }, orig.dummy.x);
    this.mountNum(dummyHost, 'Pos Y', -0.4, 0.4, 0.001, 'offset Y when following, else pose Y', () => beat.dummy.y, (n) => { beat.dummy.y = n; }, orig.dummy.y);
    this.mountNum(dummyHost, 'Pos Z', -0.4, 0.4, 0.001, 'offset Z when following, else pose Z', () => beat.dummy.z, (n) => { beat.dummy.z = n; }, orig.dummy.z);
    this.mountNum(dummyHost, 'Pitch', -3.15, 3.15, 0.01, 'dummy pitch', () => beat.dummy.pitch, (n) => { beat.dummy.pitch = n; }, orig.dummy.pitch);
    this.mountNum(dummyHost, 'Yaw', -3.15, 3.15, 0.01, 'dummy yaw', () => beat.dummy.yaw, (n) => { beat.dummy.yaw = n; }, orig.dummy.yaw);
    this.mountNum(dummyHost, 'Roll', -3.15, 3.15, 0.01, 'dummy roll', () => beat.dummy.roll, (n) => { beat.dummy.roll = n; }, orig.dummy.roll);

    this.mountReloadHandSliders(handHost, beat.hand, orig.hand);

    this.mountNum(shellHost, 'Hold mag', 0, 1, 0.01, 'fresh mag in the left hand', () => beat.magHold, (n) => { beat.magHold = n; }, orig.magHold);
    this.mountNum(shellHost, 'In gun', 0, 1, 0.01, 'mag seated in the well', () => beat.magSeated, (n) => { beat.magSeated = n; }, orig.magSeated);
    this.mountNum(shellHost, 'Mag X', -0.2, 0.2, 0.001, 'held mag vs dummy (thumb <-> pinky)', () => beat.held.x, (n) => { beat.held.x = n; }, orig.held.x);
    this.mountNum(shellHost, 'Mag Y', -0.2, 0.2, 0.001, 'held mag vs dummy (into / off palm)', () => beat.held.y, (n) => { beat.held.y = n; }, orig.held.y);
    this.mountNum(shellHost, 'Mag Z', -0.2, 0.2, 0.001, 'held mag vs dummy (wrist <-> fingers)', () => beat.held.z, (n) => { beat.held.z = n; }, orig.held.z);
    this.mountNum(shellHost, 'Mag Pitch', -3.15, 3.15, 0.01, 'held mag pitch', () => beat.held.pitch, (n) => { beat.held.pitch = n; }, orig.held.pitch);
    this.mountNum(shellHost, 'Mag Yaw', -3.15, 3.15, 0.01, 'held mag yaw', () => beat.held.yaw, (n) => { beat.held.yaw = n; }, orig.held.yaw);
    this.mountNum(shellHost, 'Mag Roll', -3.15, 3.15, 0.01, 'held mag roll', () => beat.held.roll, (n) => { beat.held.roll = n; }, orig.held.roll);
  }

  private mountReloadHandSliders(handHost: HTMLElement, hand: HandAttachPose, orig: HandAttachPose): void {
    const handDefs: Array<{
      label: string;
      min: number;
      max: number;
      step: number;
      hint: string;
      key: keyof HandAttachPose;
    }> = [
      { label: 'Pos X', min: -0.22, max: 0.22, step: 0.001, hint: 'thumb <-> pinky', key: 'x' },
      { label: 'Pos Y', min: -0.22, max: 0.22, step: 0.001, hint: 'into / off the gun', key: 'y' },
      { label: 'Pos Z', min: -0.22, max: 0.22, step: 0.001, hint: 'wrist <-> fingers', key: 'z' },
      { label: 'Pitch', min: -3.15, max: 3.15, step: 0.01, hint: 'hand rx', key: 'rx' },
      { label: 'Yaw', min: -3.15, max: 3.15, step: 0.01, hint: 'hand ry', key: 'ry' },
      { label: 'Roll', min: -3.15, max: 3.15, step: 0.01, hint: 'hand rz', key: 'rz' },
      { label: 'W Pitch', min: -3.15, max: 3.15, step: 0.01, hint: 'wrist rx', key: 'wx' },
      { label: 'W Yaw', min: -3.15, max: 3.15, step: 0.01, hint: 'wrist ry', key: 'wy' },
      { label: 'W Roll', min: -3.15, max: 3.15, step: 0.01, hint: 'wrist rz / twist', key: 'wz' },
      { label: 'Scale', min: 0.5, max: 2.4, step: 0.01, hint: 'hand size', key: 'scale' },
      { label: 'Curl', min: -1.6, max: 1.0, step: 0.01, hint: 'all joints wrap', key: 'curl' },
      { label: 'Knuckle', min: -1.6, max: 1.0, step: 0.01, hint: 'ring+pinky extra wrap', key: 'knuckle' },
    ];
    for (const d of handDefs) {
      this.mountSlider(handHost, d, hand, orig);
    }
  }

  private mountNum(
    host: HTMLElement,
    label: string,
    min: number,
    max: number,
    step: number,
    hint: string,
    get: () => number,
    set: (n: number) => void,
    orig: number,
  ): void {
    const row = document.createElement('div');
    row.className = 'vm-slider-row';
    const lab = document.createElement('span');
    lab.className = 'vm-slider-label';
    lab.textContent = label;
    lab.title = hint;
    const range = document.createElement('input');
    range.type = 'range';
    range.min = String(min);
    range.max = String(max);
    range.step = String(step);
    const num = document.createElement('input');
    num.type = 'text';
    num.inputMode = 'decimal';
    num.autocomplete = 'off';
    num.spellcheck = false;
    num.className = 'vm-slider-num';
    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'vm-reset-one';
    reset.title = `Reset ${label}`;
    reset.textContent = '↺';

    const apply = (raw: number) => {
      const n = Math.min(max, Math.max(min, raw));
      set(n);
      range.value = String(n);
      num.value = n.toFixed(3);
      this.refreshSnippet();
    };

    range.addEventListener('input', () => apply(Number(range.value)));
    num.addEventListener('input', () => {
      const n = Number(num.value.replace(',', '.'));
      if (!Number.isFinite(n)) return;
      apply(n);
    });
    reset.addEventListener('click', () => apply(orig));

    const v = get();
    range.value = String(v);
    num.value = v.toFixed(3);
    row.append(lab, range, num, reset);
    host.appendChild(row);
  }

  private buildThumbSliders(): void {
    const host = this.panel.querySelector('#hand-thumb-sliders') as HTMLElement | null;
    if (!host) return;
    host.replaceChildren();
    const arms = this.arms();
    if (!arms) return;
    const pose = arms.thumbPoseOf(this.side, this.weapon);
    const orig = bakedThumbPose(this.weapon, this.side);
    const thenar = arms.thenarShape(this.weapon);
    const thenarOrig = bakedThenarShape(this.weapon);
    const bulk = document.createElement('div');
    bulk.className = 'vm-calib-subhead';
    bulk.textContent = 'Palm';
    host.appendChild(bulk);
    this.mountThenarSlider(
      host,
      {
        label: 'Squeeze',
        min: 0.25,
        max: 1.2,
        step: 0.01,
        hint: 'this hand only — palm length toward fingers (lower = shorter)',
        key: this.side === 'R' ? 'palmLenR' : 'palmLenL',
      },
      thenar,
      thenarOrig,
    );
    const thenarHead = document.createElement('div');
    thenarHead.className = 'vm-calib-subhead';
    thenarHead.textContent = 'Thenar bulk (closest to hand)';
    host.appendChild(thenarHead);
    const thenarDefs: Array<{
      label: string;
      min: number;
      max: number;
      step: number;
      hint: string;
      key: keyof ThenarShape;
    }> = [
      { label: 'Wide', min: 0.4, max: 3.4, step: 0.02, hint: 'thickness across', key: 'wide' },
      { label: 'Flat', min: 0.4, max: 3.4, step: 0.02, hint: 'thickness pad ↔ back', key: 'flat' },
      { label: 'Pos X', min: 0, max: 0.03, step: 0.001, hint: 'out from palm (smaller = closer)', key: 'x' },
      { label: 'Pos Y', min: -0.02, max: 0.02, step: 0.001, hint: 'up / into palm', key: 'y' },
      { label: 'Pos Z', min: -0.03, max: 0.04, step: 0.001, hint: 'wrist ↔ fingers', key: 'z' },
    ];
    for (const d of thenarDefs) {
      this.mountThenarSlider(host, d, thenar, thenarOrig);
    }
    const midHead = document.createElement('div');
    midHead.className = 'vm-calib-subhead';
    midHead.textContent = 'Knuckle bulk (mid)';
    host.appendChild(midHead);
    for (const d of [
      { label: 'Wide', min: 0.4, max: 3.4, step: 0.02, hint: 'mid thickness across', key: 'midWide' as const },
      { label: 'Flat', min: 0.4, max: 3.4, step: 0.02, hint: 'mid thickness pad ↔ back', key: 'midFlat' as const },
    ]) {
      this.mountThenarSlider(host, d, thenar, thenarOrig);
    }
    const tipHead = document.createElement('div');
    tipHead.className = 'vm-calib-subhead';
    tipHead.textContent = 'Tip bulk (final)';
    host.appendChild(tipHead);
    for (const d of [
      { label: 'Wide', min: 0.4, max: 3.4, step: 0.02, hint: 'tip thickness across', key: 'tipWide' as const },
      { label: 'Flat', min: 0.4, max: 3.4, step: 0.02, hint: 'tip thickness pad ↔ back', key: 'tipFlat' as const },
    ]) {
      this.mountThenarSlider(host, d, thenar, thenarOrig);
    }
    const joints: Array<{ key: keyof ThumbPose; title: string }> = [
      { key: 'base', title: 'Thenar (from wrist)' },
      { key: 'mid', title: 'Knuckle (mid)' },
      { key: 'tip', title: 'Tip (top)' },
    ];
    const axes: Array<{ axis: 0 | 1 | 2; label: string; hint: string }> = [
      { axis: 0, label: 'Pitch', hint: '− curls toward palm / down' },
      { axis: 1, label: 'Yaw', hint: 'around the grip' },
      { axis: 2, label: 'Roll', hint: 'twist' },
    ];
    for (const joint of joints) {
      const sub = document.createElement('div');
      sub.className = 'vm-calib-subhead';
      sub.textContent = joint.title;
      host.appendChild(sub);
      for (const ax of axes) {
        this.mountThumbSlider(host, {
          label: ax.label,
          hint: `${joint.title} ${ax.hint}`,
          joint: joint.key,
          axis: ax.axis,
        }, pose, orig);
      }
    }
  }

  private mountThumbSlider(
    host: HTMLElement,
    def: { label: string; hint: string; joint: keyof ThumbPose; axis: 0 | 1 | 2 },
    pose: ThumbPose,
    orig: ThumbPose,
  ): void {
    const min = -3.15;
    const max = 3.15;
    const step = 0.01;
    const row = document.createElement('div');
    row.className = 'vm-slider-row';
    const lab = document.createElement('span');
    lab.className = 'vm-slider-label';
    lab.textContent = def.label;
    lab.title = def.hint;
    const range = document.createElement('input');
    range.type = 'range';
    range.min = String(min);
    range.max = String(max);
    range.step = String(step);
    const num = document.createElement('input');
    num.type = 'text';
    num.inputMode = 'decimal';
    num.autocomplete = 'off';
    num.spellcheck = false;
    num.className = 'vm-slider-num';
    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'vm-reset-one';
    reset.title = `Reset ${def.hint}`;
    reset.textContent = '↺';

    const apply = (raw: number) => {
      const n = Math.min(max, Math.max(min, raw));
      pose[def.joint][def.axis] = n;
      this.arms()?.useWeapon(this.weapon);
      range.value = String(n);
      num.value = n.toFixed(3);
      this.refreshSnippet();
    };

    range.addEventListener('input', () => apply(Number(range.value)));
    num.addEventListener('input', () => {
      const n = Number(num.value.replace(',', '.'));
      if (!Number.isFinite(n)) return;
      apply(n);
    });
    reset.addEventListener('click', () => apply(orig[def.joint][def.axis]));

    const v = pose[def.joint][def.axis];
    range.value = String(v);
    num.value = v.toFixed(3);
    row.append(lab, range, num, reset);
    host.appendChild(row);
  }

  private mountThenarSlider(
    host: HTMLElement,
    def: { label: string; min: number; max: number; step: number; hint: string; key: keyof ThenarShape },
    shape: ThenarShape,
    orig: ThenarShape,
  ): void {
    const row = document.createElement('div');
    row.className = 'vm-slider-row';
    const lab = document.createElement('span');
    lab.className = 'vm-slider-label';
    lab.textContent = def.label;
    lab.title = def.hint;
    const range = document.createElement('input');
    range.type = 'range';
    range.min = String(def.min);
    range.max = String(def.max);
    range.step = String(def.step);
    const num = document.createElement('input');
    num.type = 'text';
    num.inputMode = 'decimal';
    num.autocomplete = 'off';
    num.spellcheck = false;
    num.className = 'vm-slider-num';
    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'vm-reset-one';
    reset.title = `Reset ${def.label}`;
    reset.textContent = '↺';

    const apply = (raw: number) => {
      const n = Math.min(def.max, Math.max(def.min, raw));
      shape[def.key] = n;
      this.arms()?.applyThenar(shape);
      range.value = String(n);
      num.value = n.toFixed(3);
      this.refreshSnippet();
    };

    range.addEventListener('input', () => apply(Number(range.value)));
    num.addEventListener('input', () => {
      const n = Number(num.value.replace(',', '.'));
      if (!Number.isFinite(n)) return;
      apply(n);
    });
    reset.addEventListener('click', () => apply(orig[def.key]));

    const v = shape[def.key];
    range.value = String(v);
    num.value = v.toFixed(3);
    row.append(lab, range, num, reset);
    host.appendChild(row);
  }

  private pistolRig() {
    return this.weapon === 'pistol' ? this.viewmodel.getWeapon('pistol') : null;
  }

  private buildGripSliders(): void {
    const host = this.panel.querySelector('#hand-grip-sliders') as HTMLElement | null;
    if (!host) return;
    host.replaceChildren();
    this.gripRows = [];
    const rig = this.pistolRig();
    if (!rig) return;
    this.gripShape = readM9GripShape(rig);
    const orig = defaultM9GripShape();
    const defs: Array<{
      label: string;
      min: number;
      max: number;
      step: number;
      hint: string;
      key: keyof M9GripShape;
    }> = [
      { label: 'Pitch', min: -0.8, max: 0.8, step: 0.01, hint: 'rake: − leans bottom toward you', key: 'pitch' },
      { label: 'Pos Y', min: -0.06, max: 0.04, step: 0.001, hint: 'up / down', key: 'y' },
      { label: 'Pos Z', min: 0.0, max: 0.08, step: 0.001, hint: 'toward / away from muzzle', key: 'z' },
      { label: 'Height', min: 0.05, max: 0.14, step: 0.001, hint: 'overall length', key: 'height' },
      { label: 'Top W', min: 0.008, max: 0.04, step: 0.001, hint: 'width at the top', key: 'topW' },
      { label: 'Bot W', min: 0.012, max: 0.055, step: 0.001, hint: 'width at the bottom', key: 'botW' },
      { label: 'Depth', min: 0.018, max: 0.05, step: 0.001, hint: 'front-to-back thickness', key: 'depth' },
    ];
    for (const d of defs) {
      this.gripRows.push(this.mountGripSlider(host, d, orig));
    }
  }

  private applyGrip(): void {
    const rig = this.pistolRig();
    if (!rig) return;
    applyM9GripShape(rig, this.gripShape);
    this.refreshSnippet();
  }

  private mountGripSlider(
    host: HTMLElement,
    def: { label: string; min: number; max: number; step: number; hint: string; key: keyof M9GripShape },
    orig: M9GripShape,
  ): SliderRow {
    const row = document.createElement('div');
    row.className = 'vm-slider-row';
    const lab = document.createElement('span');
    lab.className = 'vm-slider-label';
    lab.textContent = def.label;
    lab.title = def.hint;
    const range = document.createElement('input');
    range.type = 'range';
    range.min = String(def.min);
    range.max = String(def.max);
    range.step = String(def.step);
    const num = document.createElement('input');
    num.type = 'text';
    num.inputMode = 'decimal';
    num.autocomplete = 'off';
    num.spellcheck = false;
    num.className = 'vm-slider-num';
    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'vm-reset-one';
    reset.title = `Reset ${def.label}`;
    reset.textContent = '↺';

    const apply = (raw: number) => {
      const n = Math.min(def.max, Math.max(def.min, raw));
      this.gripShape[def.key] = n;
      range.value = String(n);
      num.value = n.toFixed(3);
      this.applyGrip();
    };

    range.addEventListener('input', () => apply(Number(range.value)));
    num.addEventListener('input', () => {
      const n = Number(num.value.replace(',', '.'));
      if (!Number.isFinite(n)) return;
      apply(n);
    });
    reset.addEventListener('click', () => apply(orig[def.key]));

    const v = this.gripShape[def.key];
    range.value = String(v);
    num.value = v.toFixed(3);
    row.append(lab, range, num, reset);
    host.appendChild(row);
    return { range, num };
  }

  private mountSlider(
    host: HTMLElement,
    def: { label: string; min: number; max: number; step: number; hint: string; key: keyof HandAttachPose },
    pose: HandAttachPose,
    orig: HandAttachPose,
  ): SliderRow {
    const row = document.createElement('div');
    row.className = 'vm-slider-row';
    const lab = document.createElement('span');
    lab.className = 'vm-slider-label';
    lab.textContent = def.label;
    lab.title = def.hint;
    const range = document.createElement('input');
    range.type = 'range';
    range.min = String(def.min);
    range.max = String(def.max);
    range.step = String(def.step);
    const num = document.createElement('input');
    num.type = 'text';
    num.inputMode = 'decimal';
    num.autocomplete = 'off';
    num.spellcheck = false;
    num.className = 'vm-slider-num';
    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'vm-reset-one';
    reset.title = `Reset ${def.label}`;
    reset.textContent = '↺';

    const apply = (raw: number) => {
      const n = Math.min(def.max, Math.max(def.min, raw));
      pose[def.key] = n;
      if (def.key === 'curl' || def.key === 'knuckle') this.arms()?.useWeapon(this.weapon);
      range.value = String(n);
      num.value = n.toFixed(3);
      this.refreshSnippet();
    };

    range.addEventListener('input', () => apply(Number(range.value)));
    num.addEventListener('input', () => {
      const n = Number(num.value.replace(',', '.'));
      if (!Number.isFinite(n)) return;
      apply(n);
    });
    reset.addEventListener('click', () => apply(orig[def.key]));

    const v = pose[def.key];
    range.value = String(v);
    num.value = v.toFixed(3);
    row.append(lab, range, num, reset);
    host.appendChild(row);
    return { range, num };
  }

  private resetSide(): void {
    this.arms()?.resetAttach(this.weapon, this.side, this.aiming);
    this.arms()?.resetThumb(this.weapon, this.side);
    this.buildSliders();
    this.buildThumbSliders();
    this.refreshSnippet();
  }

  private resetGun(): void {
    this.arms()?.resetAttach(this.weapon);
    this.arms()?.resetThumb(this.weapon);
    this.arms()?.resetThenar(this.weapon);
    if (this.weapon === 'sawedoff' || isMagReloadWeapon(this.weapon)) this.viewmodel.resetReloadHoldTune();
    this.buildSliders();
    this.buildThumbSliders();
    this.buildReloadSliders();
    this.refreshSnippet();
  }

  private resetThumb(): void {
    this.arms()?.resetThumb(this.weapon, this.side);
    this.arms()?.resetThenar();
    this.buildThumbSliders();
    this.refreshSnippet();
  }

  private resetGrip(): void {
    this.gripShape = defaultM9GripShape();
    this.applyGrip();
    this.buildGripSliders();
  }

  private resetView(): void {
    const cam = this.viewmodel.camera;
    cam.position.set(0, 0, 0);
    cam.up.set(0, 1, 0);
    cam.rotation.order = 'YXZ';
    cam.rotation.set(0, 0, 0);
    cam.quaternion.identity();
    this.orbit.target.copy(FPS_TARGET);
    this.orbit.update();
    this.statusEl.textContent = 'View reset to FPS hip/aim camera.';
  }

  private snippetText(): string {
    const arms = this.arms();
    if (!arms) return '';
    const lines = [`=== hand attach (${this.weapon}) ===`, ''];
    for (const side of ['R', 'L'] as const) {
      const hip = arms.poseOf(side, this.weapon, false);
      const ads = arms.poseOf(side, this.weapon, true);
      const t = arms.thumbPoseOf(side, this.weapon);
      lines.push(
        `${this.weapon}.${side} hip: { x: ${fmt(hip.x)}, y: ${fmt(hip.y)}, z: ${fmt(hip.z)}, rx: ${fmt(hip.rx)}, ry: ${fmt(hip.ry)}, rz: ${fmt(hip.rz)}, wx: ${fmt(hip.wx)}, wy: ${fmt(hip.wy)}, wz: ${fmt(hip.wz)}, scale: ${fmt(hip.scale)}, curl: ${fmt(hip.curl)}, knuckle: ${fmt(hip.knuckle)} }`,
      );
      lines.push(
        `${this.weapon}.${side} ads: { x: ${fmt(ads.x)}, y: ${fmt(ads.y)}, z: ${fmt(ads.z)}, rx: ${fmt(ads.rx)}, ry: ${fmt(ads.ry)}, rz: ${fmt(ads.rz)}, wx: ${fmt(ads.wx)}, wy: ${fmt(ads.wy)}, wz: ${fmt(ads.wz)}, scale: ${fmt(ads.scale)}, curl: ${fmt(ads.curl)}, knuckle: ${fmt(ads.knuckle)} }`,
      );
      lines.push(
        `${this.weapon}.${side} thumb: { base: [${fmt(t.base[0])}, ${fmt(t.base[1])}, ${fmt(t.base[2])}], mid: [${fmt(t.mid[0])}, ${fmt(t.mid[1])}, ${fmt(t.mid[2])}], tip: [${fmt(t.tip[0])}, ${fmt(t.tip[1])}, ${fmt(t.tip[2])}] }`,
      );
    }
    const th = arms.thenarShape(this.weapon);
    lines.push('');
    lines.push(
      `thenar: { wide: ${fmt(th.wide)}, flat: ${fmt(th.flat)}, midWide: ${fmt(th.midWide)}, midFlat: ${fmt(th.midFlat)}, tipWide: ${fmt(th.tipWide)}, tipFlat: ${fmt(th.tipFlat)}, x: ${fmt(th.x)}, y: ${fmt(th.y)}, z: ${fmt(th.z)}, palmLenR: ${fmt(th.palmLenR)}, palmLenL: ${fmt(th.palmLenL)} }`,
    );
    if (this.weapon === 'pistol') {
      const s = this.gripShape;
      lines.push('');
      lines.push(
        `m9 grip: { pitch: ${fmt(s.pitch)}, y: ${fmt(s.y)}, z: ${fmt(s.z)}, height: ${fmt(s.height)}, topW: ${fmt(s.topW)}, botW: ${fmt(s.botW)}, depth: ${fmt(s.depth)} }`,
      );
    }
    if (this.weapon === 'sawedoff') {
      lines.push('');
      lines.push('=== double reload clip (sawedoff) ===');
      for (const id of DOUBLE_RELOAD_BEAT_ORDER) {
        const b = this.viewmodel.reloadBeat(id);
        if (b) lines.push(...fmtReloadBeat(b));
      }
    }
    if (isMagReloadWeapon(this.weapon)) {
      lines.push('');
      lines.push(`=== ${this.weapon} mag reload clip ===`);
      for (const id of PISTOL_RELOAD_BEAT_ORDER) {
        const b = this.viewmodel.pistolReloadBeat(id);
        if (b) lines.push(...fmtPistolReloadBeat(b));
      }
    }
    if (this.weapon === 'punch') {
      for (const id of PUNCH_CLIP_ORDER) {
        const clip = this.viewmodel.punchClip(id);
        lines.push('');
        lines.push(`=== punch clip (${id}) ===`);
        lines.push(`duration: ${fmt(clip.duration)}`);
        for (const k of clip.keys) lines.push(...fmtPunchKey(k));
      }
    }
    lines.push('');
    lines.push('Paste this in chat to bake the attach / thumb / handle / reload clip.');
    return lines.join('\n');
  }

  private refreshSnippet(): void {
    this.snippetEl.textContent = this.snippetText();
  }

  private async copySnippet(): Promise<void> {
    const text = this.snippetText();
    try {
      await navigator.clipboard.writeText(text);
      this.statusEl.textContent = 'Copied — paste that block in chat and I will apply it.';
    } catch {
      console.log(text);
      this.statusEl.textContent = 'Clipboard blocked — select the box below, or check the console.';
    }
  }
}

function fmt(n: number): string {
  const s = n.toFixed(3);
  return s === '-0.000' ? '0.000' : s;
}

function fmtPistolReloadBeat(b: PistolReloadBeat): string[] {
  const g = b.gun;
  const d = b.dummy;
  const h = b.hand;
  const m = b.held;
  return [
    '',
    `beat ${b.id} t=${fmt(b.t)}`,
    `  gun: { x: ${fmt(g.x)}, y: ${fmt(g.y)}, z: ${fmt(g.z)}, pitch: ${fmt(g.pitch)}, yaw: ${fmt(g.yaw)}, roll: ${fmt(g.roll)} }`,
    `  dummyBlend: ${fmt(b.dummyBlend)}`,
    `  followGrip: ${fmt(b.followGrip)}`,
    `  dummy: { x: ${fmt(d.x)}, y: ${fmt(d.y)}, z: ${fmt(d.z)}, pitch: ${fmt(d.pitch)}, yaw: ${fmt(d.yaw)}, roll: ${fmt(d.roll)} }`,
    `  hand: { x: ${fmt(h.x)}, y: ${fmt(h.y)}, z: ${fmt(h.z)}, rx: ${fmt(h.rx)}, ry: ${fmt(h.ry)}, rz: ${fmt(h.rz)}, wx: ${fmt(h.wx)}, wy: ${fmt(h.wy)}, wz: ${fmt(h.wz)}, scale: ${fmt(h.scale)}, curl: ${fmt(h.curl)}, knuckle: ${fmt(h.knuckle)} }`,
    `  held: { x: ${fmt(m.x)}, y: ${fmt(m.y)}, z: ${fmt(m.z)}, pitch: ${fmt(m.pitch)}, yaw: ${fmt(m.yaw)}, roll: ${fmt(m.roll)} }`,
    `  magHold: ${fmt(b.magHold)}  magSeated: ${fmt(b.magSeated)}`,
  ];
}

function fmtReloadBeat(b: DoubleReloadBeat): string[] {
  const g = b.gun;
  const d = b.dummy;
  const h = b.hand;
  const p = b.pair;
  return [
    '',
    `beat ${b.id} t=${fmt(b.t)}`,
    `  gun: { x: ${fmt(g.x)}, y: ${fmt(g.y)}, z: ${fmt(g.z)}, pitch: ${fmt(g.pitch)}, yaw: ${fmt(g.yaw)}, roll: ${fmt(g.roll)} }`,
    `  breakX: ${fmt(b.breakX)}`,
    `  dummyBlend: ${fmt(b.dummyBlend)}`,
    `  followGrip: ${fmt(b.followGrip)}`,
    `  dummy: { x: ${fmt(d.x)}, y: ${fmt(d.y)}, z: ${fmt(d.z)}, pitch: ${fmt(d.pitch)}, yaw: ${fmt(d.yaw)}, roll: ${fmt(d.roll)} }`,
    `  hand: { x: ${fmt(h.x)}, y: ${fmt(h.y)}, z: ${fmt(h.z)}, rx: ${fmt(h.rx)}, ry: ${fmt(h.ry)}, rz: ${fmt(h.rz)}, wx: ${fmt(h.wx)}, wy: ${fmt(h.wy)}, wz: ${fmt(h.wz)}, scale: ${fmt(h.scale)}, curl: ${fmt(h.curl)}, knuckle: ${fmt(h.knuckle)} }`,
    `  pair: { x: ${fmt(p.x)}, y: ${fmt(p.y)}, z: ${fmt(p.z)}, rx: ${fmt(p.rx)}, ry: ${fmt(p.ry)}, rz: ${fmt(p.rz)} }`,
    `  pairVis: ${fmt(b.pairVis)}  chamberVis: ${fmt(b.chamberVis)}`,
  ];
}

function fmtPunchKey(k: PunchKey): string[] {
  const r = k.right;
  const l = k.left;
  return [
    '',
    `beat ${k.id} t=${fmt(k.t)}`,
    `  right: { x: ${fmt(r.x)}, y: ${fmt(r.y)}, z: ${fmt(r.z)}, rx: ${fmt(r.rx)}, ry: ${fmt(r.ry)}, rz: ${fmt(r.rz)} }`,
    `  left: { x: ${fmt(l.x)}, y: ${fmt(l.y)}, z: ${fmt(l.z)}, rx: ${fmt(l.rx)}, ry: ${fmt(l.ry)}, rz: ${fmt(l.rz)} }`,
  ];
}

function panelMarkup(): string {
  const guns = WEAPON_ORDER.map(
    (id) => `<button id="hand-w-${id}" class="calib-btn" type="button">${LABELS[id]}</button>`,
  ).join('');
  return `
    <div class="vm-calib-title" id="hand-calib-title">Hand attach studio</div>
    <p class="vm-calib-status" id="hand-calib-status"></p>
    <div class="vm-calib-row vm-calib-row-wrap">${guns}</div>
    <div class="vm-calib-row">
      <button id="hand-side-r" class="calib-btn" type="button">Right hand</button>
      <button id="hand-side-l" class="calib-btn" type="button">Left hand</button>
    </div>
    <div class="vm-calib-row">
      <button id="hand-hip" class="calib-btn" type="button">Hip</button>
      <button id="hand-ads" class="calib-btn" type="button">Aiming (ADS)</button>
      <button id="hand-reset-view" class="calib-btn primary" type="button">Reset FPS view</button>
      <button id="hand-clone" class="calib-btn" type="button">Show clone</button>
      <button id="hand-clone-crouch" class="calib-btn" type="button">Crouch clone</button>
    </div>
    <div class="vm-calib-row">
      <button id="hand-view-front" class="calib-btn active" type="button">Front View</button>
      <button id="hand-view-side" class="calib-btn" type="button">Side View</button>
      <button id="hand-view-top" class="calib-btn" type="button">Top View</button>
      <button id="hand-play-reload" class="calib-btn hidden" type="button">Play reload</button>
      <button id="hand-play-punch" class="calib-btn hidden" type="button">Play punch</button>
    </div>
    <div class="vm-calib-section">
      <div class="vm-calib-section-title" id="hand-section-title">Right hand on grip_r</div>
      <div id="hand-calib-sliders"></div>
      <button id="hand-reset-side" class="calib-btn vm-section-reset" type="button">Reset this hand</button>
    </div>
    <div class="vm-calib-section">
      <div class="vm-calib-section-title" id="hand-thumb-title">Right thumb joints</div>
      <div id="hand-thumb-sliders"></div>
      <button id="hand-reset-thumb" class="calib-btn vm-section-reset" type="button">Reset thumb</button>
    </div>
    <div class="vm-calib-section" id="hand-grip-section">
      <div class="vm-calib-section-title">Pistol handle</div>
      <div id="hand-grip-sliders"></div>
      <button id="hand-reset-grip" class="calib-btn vm-section-reset" type="button">Reset handle</button>
    </div>
    <div class="vm-calib-section hidden" id="hand-punch-section">
      <div class="vm-calib-section-title">Punch clip — park a beat, then Play</div>
      <p class="calib-hint">Init/end follow hip (lowered) or ADS (guard). Pos 1 / Pos 2 are the strike. Playback interpolates.</p>
      <div class="vm-calib-row vm-calib-row-wrap" id="hand-punch-clips"></div>
      <div class="vm-calib-row vm-calib-row-wrap" id="hand-punch-beats"></div>
      <div class="vm-calib-subhead">Right fist target</div>
      <div id="hand-punch-right-sliders"></div>
      <div class="vm-calib-subhead">Left fist target</div>
      <div id="hand-punch-left-sliders"></div>
      <div class="vm-calib-row">
        <button id="hand-reset-punch-beat" class="calib-btn vm-section-reset" type="button">Reset this beat</button>
        <button id="hand-reset-punch" class="calib-btn vm-section-reset" type="button">Reset this clip</button>
      </div>
    </div>
    <div class="vm-calib-section hidden" id="hand-reload-section">
      <div class="vm-calib-section-title">Reload clip — park a beat, then Play</div>
      <p class="calib-hint" id="hand-reload-hint">Each beat is a pose. Playback interpolates between them. Park one, set gun / dummy / hand / shells, copy snippet.</p>
      <div class="vm-calib-row vm-calib-row-wrap" id="hand-reload-beats"></div>
      <div class="vm-calib-subhead" id="hand-reload-gun-title">Gun (whole model + barrel break)</div>
      <div id="hand-reload-gun-sliders"></div>
      <div class="vm-calib-subhead" id="hand-reload-dummy-title">Hold-shells dummy (left hand target)</div>
      <div id="hand-reload-dummy-sliders"></div>
      <div class="vm-calib-subhead">Left hand on dummy</div>
      <div id="hand-reload-hand-sliders"></div>
      <div class="vm-calib-subhead" id="hand-reload-prop-title">Shells in palm</div>
      <div id="hand-reload-shell-sliders"></div>
      <div class="vm-calib-row">
        <button id="hand-reset-reload-beat" class="calib-btn vm-section-reset" type="button">Reset this beat</button>
        <button id="hand-reset-reload" class="calib-btn vm-section-reset" type="button">Reset all beats</button>
      </div>
    </div>
    <div class="vm-calib-section">
      <div class="vm-calib-section-title">Copy these numbers</div>
      <pre id="hand-calib-snippet" class="calib-values vm-calib-snippet"></pre>
      <div class="vm-calib-row">
        <button id="hand-reset-gun" class="calib-btn" type="button">Reset both hands</button>
        <button id="hand-copy" class="calib-btn primary" type="button">Copy snippet</button>
      </div>
    </div>
    <p class="calib-hint">LMB drag = orbit &nbsp;·&nbsp; wheel = zoom &nbsp;·&nbsp; RMB = pan &nbsp;·&nbsp; V = Front / FPS view<br>
    Front / Side / Top = camera between you and the clone &nbsp;·&nbsp; C = crouch / stand clone<br>
    1–8 = weapon &nbsp;·&nbsp; Q / E = left / right &nbsp;·&nbsp; A = hip ↔ ADS<br>
    Double / mag guns: [ ] or H = step reload beats &nbsp;·&nbsp; R = play reload<br>
    Shotgun: R = play pump &nbsp;·&nbsp; Fists: [ ] or H = step punch beats &nbsp;·&nbsp; R = play punch<br>
    URLs: <code>?calib=hands</code> &nbsp; <code>?calib=hands&amp;weapon=punch</code></p>
  `;
}
